import cx from "classnames";
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiGetTemplateByName } from "@/api/ApiGetTemplateByName";
import { ApiGetTemplateList, type LoadedTemplate } from "@/api/ApiGetTemplateList";
import { default as ClearIcon } from "@/appInterface/windows/stickersWindow/ClearIcon.svg?react";
import style from "@/appInterface/windows/stickersWindow/stickersWindow.module.css";
import { canv } from "@/canv/global";
import { debounce } from "@/canv/utils";
import styles from "./TemplatesWindow.module.css";

// WARNING!!! В шаблонах не должно быть стрелок и mindMap.
export const TemplatesWindow = () => {
    const [templates, setTemplates] = useState<LoadedTemplate[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<LoadedTemplate[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchValue, setSearchValue] = useState("");

    const getTemplates = async () => {
        setIsLoading(true);
        const list = await ApiGetTemplateList();
        if (list && list?.length > 0) {
            setTemplates(list);
            setFilteredTemplates(list);
            setIsLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        getTemplates();
    }, []);

    const handleSelectedTemplate = async (tn: string) => {
        await ApiGetTemplateByName(tn);
    };

    const onFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const onBlur = () => {
        canv.appInterface.inputFocused = 0;
    };

    const onSearch = useCallback(
        (text: string) => {
            if (text) {
                if (!text || templates.length === 0) return;

                const searchWords = text.toLowerCase().split(/\s+/).filter(Boolean);

                const filteredValues = [...templates];
                const result = filteredValues.filter((template) => {
                    if (!template.title) return false;
                    const titleLower = template.title.toLowerCase();
                    return searchWords.every((word) => titleLower.includes(word));
                });

                setFilteredTemplates(result);
            } else {
                setFilteredTemplates(templates);
            }
        },
        [templates],
    );

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                onSearch(value);
            }, 500),
        [onSearch],
    );

    const onChangeSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        debouncedSearch(value);
    };

    const resetSearch = useCallback(() => {
        setSearchValue("");
        onSearch("");
    }, [onSearch]);

    const onClear = () => {
        resetSearch();
        searchInputRef.current?.focus();
    };

    return (
        <div className={styles.templatesWindowOuter}>
            <div className={styles.templatesWindow}>
                <div className={styles.templatesWindowTitle}>
                    Классные шаблоны для ваших активностей
                </div>
                <div className={styles.templatesSearchBlock}>
                    <div className={styles.templatesSearchField}>
                        <input
                            ref={searchInputRef}
                            onChange={onChangeSearchValue}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            value={searchValue}
                            placeholder="Название шаблона"
                            type="text"
                            className={styles.templatesSearchFieldInput}
                        />
                        {searchValue && (
                            <button className={style.clearButton} onClick={onClear} type="button">
                                <ClearIcon />
                            </button>
                        )}
                    </div>
                </div>
                {isLoading && (
                    <div className="miniLoader">
                        <div className="lminiLoaderInner"></div>
                    </div>
                )}
                {!isLoading && (
                    <div className={cx(styles.templatesWindowContent, "scrollable")}>
                        {filteredTemplates.map((item) => (
                            <button
                                type="button"
                                key={item.templateName}
                                className={styles.templatesWindowContentItem}
                                onClick={() => handleSelectedTemplate(item.templateName)}
                            >
                                <div className={styles.templatesWindowContentItemTitle}>
                                    {item.title}
                                </div>
                                <div className={styles.templatesWindowContentItemView}>
                                    <img src={item.view} alt="pic" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                {!isLoading && filteredTemplates?.length === 0 && (
                    <div className={cx(styles.templatesWindowContent, "scrollable")}>
                        <div className={styles.notFound}>По вашему запросу шаблоны не найдены</div>
                    </div>
                )}
            </div>
        </div>
    );
};
